/**
 * Every API call the app makes, grouped by screen.
 *
 * Components call these rather than `api.get('/orders/...')` directly, so a
 * route change on the server is one edit here instead of a search across the
 * codebase - and so the shape of the API is readable in one file.
 */

import api from './api';

export const authApi = {
    bootstrap: () => api.get('/bootstrap'),
    login: (userName, password) => api.post('/auth/login', { userName, password }),
    loginWithPin: (pin) => api.post('/auth/login-pin', { pin }),
    logout: (userSessionId) => api.post('/auth/logout', { userSessionId }),
    profile: () => api.get('/auth/profile'),
    changePassword: (currentPassword, newPassword) =>
        api.post('/auth/change-password', { currentPassword, newPassword }),
    setPin: (pin) => api.post('/auth/set-pin', { pin })
};

export const lookupApi = {
    getAll: () => api.get('/lookups')
};

export const menuApi = {
    getPosMenu: () => api.get('/menu/pos'),
    lookupBarcode: (code) => api.get(`/menu/barcode/${encodeURIComponent(code)}`),

    listCategories: (params) => api.get('/menu/categories', params),
    createCategory: (data) => api.post('/menu/categories', data),
    updateCategory: (id, data) => api.put(`/menu/categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/menu/categories/${id}`),

    listItems: (params) => api.get('/menu/items', params),
    getItem: (id) => api.get(`/menu/items/${id}`),
    createItem: (data) => api.post('/menu/items', data),
    updateItem: (id, data) => api.put(`/menu/items/${id}`, data),
    deleteItem: (id) => api.delete(`/menu/items/${id}`),
    setAvailability: (id, isAvailable) => api.patch(`/menu/items/${id}/availability`, { isAvailable }),
    setVariants: (id, variants) => api.put(`/menu/items/${id}/variants`, { variants }),
    setModifierGroups: (id, modifierGroupIds) =>
        api.put(`/menu/items/${id}/modifier-groups`, { modifierGroupIds }),
    setDealComponents: (id, components) => api.put(`/menu/items/${id}/deal-components`, { components }),
    listModifierGroups: () => api.get('/menu/modifier-groups'),

    /** Upload a menu photo. Returns { path } to store on the item. */
    uploadImage: (file) => api.upload('/uploads/menu-image', file),

    /**
     * Delete an uploaded image. The server refuses while any item still points
     * at it, so this is safe to call speculatively when a picker is tidying up
     * after a photo the user changed their mind about.
     */
    deleteImage: (path) => api.delete('/uploads/image', { body: { path } })
};

export const orderApi = {
    save: (payload) => api.post('/orders', payload),
    get: (id) => api.get(`/orders/${id}`),
    list: (params) => api.get('/orders', params),
    listHeld: () => api.get('/orders/held'),
    listOpen: () => api.get('/orders/open'),
    settle: (id, payload) => api.post(`/orders/${id}/settle`, payload),
    hold: (id, holdLabel) => api.post(`/orders/${id}/hold`, { holdLabel }),
    void: (id, payload) => api.post(`/orders/${id}/void`, payload),
    sendToKitchen: (id) => api.post(`/orders/${id}/kitchen`),

    getReceipt: (id, templateId) => api.get(`/orders/${id}/receipt`, { templateId }),
    confirmPrint: (id) => api.post(`/orders/${id}/receipt/printed`),
    getKitchenTicket: (kotId) => api.get(`/kot/${kotId}`)
};

export const tableApi = {
    list: () => api.get('/tables'),
    setStatus: (id, status, currentOrderId) =>
        api.patch(`/tables/${id}/status`, { status, currentOrderId })
};

export const customerApi = {
    search: (search) => api.get('/customers', { search }),
    get: (id) => api.get(`/customers/${id}`),
    create: (data) => api.post('/customers', data)
};

export const shiftApi = {
    current: () => api.get('/shifts/current'),
    open: (openingCash) => api.post('/shifts/open', { openingCash }),
    close: (id, countedCash, remarks) => api.post(`/shifts/${id}/close`, { countedCash, remarks }),
    report: (id) => api.get(`/shifts/${id}/report`),
    cashMovement: (movementType, amount, reason) =>
        api.post('/shifts/cash-movement', { movementType, amount, reason })
};

export const inventoryApi = {
    listItems: (params) => api.get('/inventory/items', params),
    getItem: (id) => api.get(`/inventory/items/${id}`),
    createItem: (data) => api.post('/inventory/items', data),
    updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
    getLedger: (id, params) => api.get(`/inventory/items/${id}/ledger`, params),
    listCategories: () => api.get('/inventory/categories'),
    listLowStock: () => api.get('/inventory/low-stock'),

    listSuppliers: (params) => api.get('/inventory/suppliers',
        typeof params === 'string' ? { search: params } : (params || {})),
    getSupplier: (id) => api.get(`/inventory/suppliers/${id}`),
    createSupplier: (data) => api.post('/inventory/suppliers', data),
    updateSupplier: (id, data) => api.put(`/inventory/suppliers/${id}`, data),
    deactivateSupplier: (id) => api.delete(`/inventory/suppliers/${id}`),

    listRecipes: (menuItemId) => api.get('/inventory/recipes', { menuItemId }),
    getRecipe: (menuItemId) => api.get(`/inventory/recipes/${menuItemId}`),
    setRecipe: (menuItemId, lines) => api.put(`/inventory/recipes/${menuItemId}`, { lines }),

    listPurchases: (params) => api.get('/purchases', params),
    getPurchase: (id) => api.get(`/purchases/${id}`),
    createPurchase: (data) => api.post('/purchases', data),

    listAdjustments: (params) => api.get('/inventory/adjustments', params),
    createAdjustment: (data) => api.post('/inventory/adjustments', data)
};

export const accountsApi = {
    summary: () => api.get('/accounts/summary'),
    payables: () => api.get('/accounts/payables'),
    receivables: () => api.get('/accounts/receivables'),
    cashMovements: (params) => api.get('/accounts/cash-movements', params),
    paySupplier: (data) => api.post('/accounts/pay-supplier', data),
    receiveFromCustomer: (data) => api.post('/accounts/receive-customer', data)
};

export const fiscalApi = {
    status: () => api.get('/fiscal/status'),
    listQueue: (params) => api.get('/fiscal/queue', params),
    retry: (queueId) => api.post(`/fiscal/queue/${queueId}/retry`),
    retryAllFailed: () => api.post('/fiscal/queue/retry-failed'),
    cancel: (queueId) => api.delete(`/fiscal/queue/${queueId}`),
    submitOrder: (orderId) => api.post(`/fiscal/orders/${orderId}/submit`),
    getOrderDetail: (orderId) => api.get(`/fiscal/orders/${orderId}`),
    run: () => api.post('/fiscal/run'),
    saveIntegration: (data) => api.post('/fiscal/integration', data),
    updateAuthority: (id, data) => api.put(`/fiscal/authorities/${id}`, data),
    testConnection: (authorityId) => api.post('/fiscal/test-connection', { authorityId }),
    setEnabled: (enabled) => api.post('/fiscal/enabled', { enabled }),
    setActiveAuthority: (authorityCode) => api.post('/fiscal/active-authority', { authorityCode })
};

export const reportApi = {
    dashboard: (params) => api.get('/reports/dashboard', params),
    sales: (params) => api.get('/reports/sales', params),
    items: (params) => api.get('/reports/items', params),
    tax: (params) => api.get('/reports/tax', params),
    stock: (params) => api.get('/reports/stock', params),
    wastage: (params) => api.get('/reports/wastage', params),
    staff: (params) => api.get('/reports/staff', params)
};

export const setupApi = {
    listSettings: () => api.get('/settings'),
    updateSettings: (settings) => api.put('/settings', { settings }),
    getBusiness: () => api.get('/business'),

    /** The logo printed at the top of every receipt. Same flow as a menu
     *  photo: upload first, then save the returned path with the business. */
    uploadLogo: (file) => api.upload('/uploads/logo', file),
    deleteImage: (path) => api.delete('/uploads/image', { body: { path } }),

    updateBusiness: (data) => api.put('/business', data),
    listReceiptTemplates: () => api.get('/receipt-templates'),
    updateReceiptTemplate: (id, data) => api.put(`/receipt-templates/${id}`, data),
    listUsers: () => api.get('/users'),
    createUser: (data) => api.post('/users', data),
    updateUser: (id, data) => api.put(`/users/${id}`, data),
    resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
    setUserPin: (id, pin) => api.post(`/users/${id}/pin`, { pin }),
    unlockUser: (id) => api.post(`/users/${id}/unlock`),
    setUserActive: (id, isActive) => api.patch(`/users/${id}/active`, { isActive }),
    listRoles: () => api.get('/roles')
};

/**
 * Role management. Every call here is refused with a 404 for anyone but the
 * supplier's Super Admin, so the screen behind it is guarded on both sides.
 */
export const adminApi = {
    listRoles: () => api.get('/admin/roles'),
    createRole: (data) => api.post('/admin/roles', data),
    updateRole: (id, data) => api.put(`/admin/roles/${id}`, data),
    listRoutes: () => api.get('/admin/routes'),
    getRoleRoutes: (id) => api.get(`/admin/roles/${id}/routes`),
    setRoleRoutes: (id, grants) => api.put(`/admin/roles/${id}/routes`, { grants })
};
